import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { 
  Shield, 
  ShieldCheck, 
  Smartphone,
  Key,
  Copy,
  CheckCircle,
  AlertTriangle,
  Loader2,
  LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

export const MandatoryTwoFactorSetup = () => {
  const [step, setStep] = useState(1); // 1: Intro, 2: QR code, 3: Verify, 4: Backup codes
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [error, setError] = useState("");
  
  const { logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const initiateSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(response.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to initiate 2FA setup");
      toast.error("Failed to setup 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: verifyCode });
      setStep(4);
      toast.success("2FA enabled successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleComplete = async () => {
    await refreshUser();
    toast.success("Account setup complete!");
    navigate('/');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="noise-overlay" />
      
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img 
            src={NBR_LOGO} 
            alt="National Business Register" 
            className="h-12 w-auto"
          />
          <div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">MARK</h1>
            <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
          </div>
        </div>

        <div className="swiss-card p-8" data-testid="mandatory-2fa-card">
          {/* Step 1: Introduction */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">
                  Secure Your Account
                </h2>
                <p className="text-slate-500 mb-2">
                  Two-Factor Authentication (2FA) is required to use NBR Trade Mark Monitoring.
                </p>
                <p className="text-sm text-slate-400">
                  This adds an extra layer of security to protect your trademark data.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Before you begin, you'll need:</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700">
                      <li>An authenticator app (Google Authenticator, Authy, Microsoft Authenticator)</li>
                      <li>Your smartphone ready to scan a QR code</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button 
                onClick={initiateSetup}
                disabled={loading}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                data-testid="start-2fa-setup-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Start 2FA Setup
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  onClick={handleLogout}
                  className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mx-auto"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out and return later
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: QR Code */}
          {step === 2 && setupData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Step 1: Scan QR Code
                </h3>
                <p className="text-sm text-slate-500">
                  Open your authenticator app and scan this QR code
                </p>
              </div>

              <div className="flex justify-center">
                <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
                  <img 
                    src={setupData.qr_code} 
                    alt="2FA QR Code" 
                    className="w-48 h-48"
                    data-testid="mandatory-2fa-qr-code"
                  />
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">Can't scan? Enter this code manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono break-all">
                    {setupData.secret}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(setupData.secret)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <Button 
                onClick={() => setStep(3)} 
                className="w-full"
                data-testid="mandatory-2fa-next-btn"
              >
                Next: Verify Code
              </Button>
            </motion.div>
          )}

          {/* Step 3: Verify */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Key className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Step 2: Verify Setup
                </h3>
                <p className="text-sm text-slate-500">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <Label htmlFor="verifyCode">Verification Code</Label>
                  <Input
                    id="verifyCode"
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest font-mono h-14"
                    maxLength={6}
                    autoFocus
                    data-testid="mandatory-2fa-verify-input"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600 text-center">{error}</p>
                )}

                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setStep(2)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={verifyCode.length !== 6 || loading}
                    className="flex-1"
                    data-testid="mandatory-2fa-verify-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Enable"}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Step 4: Backup Codes */}
          {step === 4 && setupData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  2FA Setup Complete!
                </h3>
                <p className="text-sm text-slate-500">
                  Save these backup codes securely - you can use them if you lose access to your authenticator.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> Each backup code can only be used once. Store them securely - they won't be shown again.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  {setupData.backup_codes.map((code, index) => (
                    <code 
                      key={index}
                      className="bg-white px-3 py-2 rounded border text-center font-mono text-sm"
                    >
                      {code}
                    </code>
                  ))}
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3"
                  onClick={() => copyToClipboard(setupData.backup_codes.join("\n"))}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Codes
                </Button>
              </div>

              <Button 
                onClick={handleComplete} 
                className="w-full h-12 bg-green-600 hover:bg-green-700"
                data-testid="mandatory-2fa-complete-btn"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Complete Setup & Continue
              </Button>
            </motion.div>
          )}

          {/* Progress indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s}
                className={`w-2 h-2 rounded-full transition-colors ${
                  s <= step ? 'bg-slate-900' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          UK Trademark Monitoring System
        </p>
      </div>
    </div>
  );
};
